import { lazy } from "react";
import StockInPage from "../pages/Inventory/StockIn/StockIn";
import { FaMoneyBillTransfer } from "react-icons/fa6";
import { FaPrescription } from "react-icons/fa";
import { BiTrendingUp, BiRupee } from "react-icons/bi";
import { GiTestTubes } from "react-icons/gi";
import {
  MdDashboard,
  MdNotificationsActive,
  MdAssignmentInd,
  MdRequestQuote,
} from "react-icons/md";
import { MedTemplatesPage } from "../pages/Settings/Medicines/templates";
import MedicineBillDetails from "../pages/Payments/MedicineBillDetails/index.jsx";

/**
 * Sidebar icons are now sourced from `react-icons` rather than the
 * mismatched `<img src="/icons/*.svg">` set. Reasons:
 *   - Vector + recolorable via the `color` prop (no per-state SVG file)
 *   - Consistent stroke-weight across the whole nav
 *   - No extra HTTP fetches — icons ship inline in the JS bundle
 *   - Each module gets a more module-specific glyph (FaTooth for
 *     Treatments, FaPrescription for Rx, GiTestTubes for Labs, etc.)
 *
 * Convention: render each icon at `size={20}` with `color="white"` so
 * the sidebar's gradient background contrast stays consistent.
 */
const ICON_SIZE = 20;
const ICON_COLOR = "white";

const Home = lazy(() => import("../pages/Home"));
const Appointments = lazy(() => import("../pages/Appointments/Appointments"));
const Settings = lazy(() => import("../pages/Settings"));
const PlanandPricingPage = lazy(
  () => import("../pages/Settings/PlanandPricing/PlanandPricing"),
);
const AdminUsers = lazy(() => import("../pages/Settings/AdminUsers"));
const Doctors = lazy(() => import("../pages/Settings/Doctors"));
const Treatments = lazy(() => import("../pages/Settings/Treatments"));
const Medicines = lazy(() => import("../pages/Settings/Medicines"));
const Labs = lazy(() => import("../pages/Settings/Labs"));
const LabsWorks = lazy(() => import("../pages/Settings/LabsWorks"));
const TreatmentsPage = lazy(() => import("../pages/Treatments"));
const InventoryPage = lazy(() => import("../pages/Inventory"));
const PatientDatabasePage = lazy(
  () => import("../pages/Treatments/PatientDatabase/PatientDatabasePage"),
);
const TreatmentDetailsPage = lazy(
  () => import("../pages/Treatments/TreatmentDetails/TreatmentDetailsPage"),
);
const ViewTreatmentDetailsPage = lazy(
  () => import("../pages/Treatments/viewTreatment/index"),
);
const EditTreatmentDetailsPage = lazy(
  () =>
    import("../pages/Treatments/EditTreatmentDetails/EditTreatmentDetailsPage"),
);
const PatientProfilePage = lazy(
  () => import("../pages/Treatments/PatientProfile/PatientProfilePage"),
);
const LabsPage = lazy(() => import("../pages/Labs/LabsPage"));
const LabOrderDetailsPage = lazy(
  () => import("../pages/Labs/LabOrderDetails/LabOrderDetails"),
);
const CreateLabOrderPage = lazy(
  () => import("../pages/Labs/CreateLabOrder/CreateLabOrder"),
);
const PaymentsPage = lazy(() => import("../pages/Payments/Payments"));
const FinancePage = lazy(() => import("../pages/Finance/index"));
const Analytics1Page = lazy(() => import("../pages/Analytics1/index"));
const PaymentDetailsPage = lazy(
  () => import("../pages/Payments/PaymentDetails/PaymentDetailsPage"),
);
const CreateInvoicePage = lazy(
  () => import("../pages/Payments/CreateInvoicePage/CreateInvoicePage"),
);
const CreateMedicineBillingPage = lazy(
  () => import("../pages/Payments/CreateMedicineBillingPage"),
);
const ExpensePage = lazy(
  () => import("../pages/Payments/Expenses/ExpensePage"),
);
const ExpenseV2Page = lazy(
  () => import("../pages/Payments/ExpensesV2/ExpenseV2Page"),
);
const PrescriptionsPage = lazy(
  () => import("../pages/Prescriptions/PrescriptionsPage"),
);
const PrescriptionDetailsPage = lazy(
  () => import("../pages/Prescriptions/PrescriptionDetails"),
);
const CreatePrescriptionPage = lazy(
  () => import("../pages/Prescriptions/CreatePrescription"),
);
const RemindersPage = lazy(() => import("../pages/Reminders/RemindersPage"));
const SupplierLIstPage = lazy(
  () => import("../pages/Inventory/suppliers/SupplierLIstPage"),
);
const StockOutPage = lazy(() => import("../pages/Inventory/StockOut/StockOut"));
const StockInOutHistoryPage = lazy(
  () => import("../pages/Inventory/StockIn_OutHistory/index"),
);
const TransactionHistoryPage = lazy(
  () => import("../pages/Payments/TransactionHistory/index"),
);

const coreRoutes = [
  {
    path: "/",
    title: "Dashboard",
    component: Home,
    dashboardItem: false,
    icon: <MdDashboard size={ICON_SIZE} color={ICON_COLOR} />,
  },
  {
    path: "/appointments",
    title: "Appointments",
    component: Appointments,
    dashboardItem: true,
    // Reverted to the original Appointments.svg per request.
    icon: (
      <img
        width={18.56}
        height={18.18}
        className=""
        src="/icons/Appointments.svg"
        alt=""
      />
    ),
  },
  {
    path: "/reminders",
    title: "Clinic Reminders",
    component: RemindersPage,
    // Sidebar entry hidden per user request. The route stays live —
    // staff still reach the page via the Appointments KPI tile click
    // and the per-patient version embedded in Patient Profile. Flip
    // back to `true` to restore the sidebar item.
    dashboardItem: false,
    icon: <MdNotificationsActive size={ICON_SIZE} color={ICON_COLOR} />,
  },
  {
    path: "/treatments",
    title: "Treatments",
    component: TreatmentsPage,
    childrens: [
      "/patient_database",
      "/treatment_details",
      "/view_treatment_details",
      "/patient_profile",
      "/edit_treatment_details",
    ],
    dashboardItem: true,
    // Clipboard with patient silhouette — Treatments is really a
    // patient-records module (patient database, profiles, treatment
    // histories), so a records glyph reads more accurately than a
    // tooth.
    icon: <MdAssignmentInd size={ICON_SIZE} color={ICON_COLOR} />,
  },
  {
    path: "/view_treatment_details",
    title: "View Treatment",
    component: ViewTreatmentDetailsPage,
    dashboardItem: false,
  },
  {
    path: "/patient_database",
    title: "Patient Database",
    component: PatientDatabasePage,
    dashboardItem: false,
  },
  {
    path: "/patient_profile",
    title: "Patient Profile",
    component: PatientProfilePage,
    dashboardItem: false,
  },
  {
    path: "/treatment_details",
    title: "Treatment Details",
    component: TreatmentDetailsPage,
    dashboardItem: false,
  },
  {
    path: "/edit_treatment_details",
    title: "Treatment Details",
    component: EditTreatmentDetailsPage,
    dashboardItem: false,
  },

  // NOTE: stock_in / stock_out / supplier_list are declared once below
  // alongside the rest of the inventory subroutes (around the
  // "/inventory" parent). The earlier duplicate triplet was removed —
  // having two entries for the same path made `routes.find(...)` lookups
  // ambiguous and caused the sidebar's `getAllChildren` to walk them
  // twice.
  {
    path: "/prescriptions",
    title: "Prescriptions",
    component: PrescriptionsPage,
    // Trailing-slash normalised: was "/prescription_details/" while the
    // sibling "/create_prescription" had no trailing slash, breaking
    // exact-match active-link detection in SideBar.
    childrens: ["/prescription_details", "/create_prescription"],
    dashboardItem: true,
    icon: <FaPrescription size={ICON_SIZE} color={ICON_COLOR} />,
  },
  {
    path: "/prescription_details",
    title: "Prescription Details",
    component: PrescriptionDetailsPage,
    dashboardItem: false,
  },
  {
    path: "/create_prescription",
    title: "Prescription Create",
    component: CreatePrescriptionPage,
    dashboardItem: false,
  },
  {
    path: "/labs",
    title: "Labs",
    component: LabsPage,
    dashboardItem: true,
    childrens: ["/lab_order_details/", "/create_lab_order"],
    // Was <GiTestTubes /> from react-icons; switched to the SVG asset
    // already shipping in public/icons so the Labs glyph reads the
    // same as the other module icons (Inventory, Payments, etc.).
    // Labs.svg is intrinsically 15×25 (≈3:5), so we set both width and
    // height to keep it within the ~20px row alignment everything else
    // uses — `object-contain` preserves the aspect ratio.
    icon: (
      <img
        width={12}
        height={20}
        className="object-contain"
        src="/icons/Labs.svg"
        alt=""
      />
    ),
  },

  {
    path: "/lab_order_details",
    title: "LabOrderDetails",
    component: LabOrderDetailsPage,
    dashboardItem: false,
  },
  {
    path: "/create_lab_order",
    title: "CreateLabOrder",
    component: CreateLabOrderPage,
    dashboardItem: false,
  },
  {
    path: "/inventory",
    title: "Inventory",
    component: InventoryPage,
    childrens: [
      // "/patient_database",
      // "/treatment_details",
      // "/patient_profile",

      "/inventory/stock_in",
      "/inventory/stock_out",
      "/inventory/supplier_list",
      "/inventory/stock_in_out_history",
    ],
    dashboardItem: true,
    // Reverted to the original InventoryIcon.svg per request.
    icon: (
      <img
        width={18.56}
        height={18.18}
        className=""
        src="/icons/InventoryIcon.svg"
        alt=""
      />
    ),
  },
  {
    path: "/inventory/stock_in",
    title: "StockIn",
    component: StockInPage,
    dashboardItem: false,
  },
  {
    path: "/inventory/stock_out",
    title: "StockOut",
    component: StockOutPage,
    dashboardItem: false,
  },
  {
    path: "/inventory/stock_in_out_history",
    title: "StockInOutHistory",
    component: StockInOutHistoryPage,
    dashboardItem: false,
  },
  {
    path: "/inventory/supplier_list",
    title: "Supplier",
    component: SupplierLIstPage,
    dashboardItem: false,
  },
  {
    path: "/payments",
    title: "Payments",
    component: PaymentsPage,
    dashboardItem: true,
    childrens: [
      "/payment_details",
      "/create_invoice",
      "/create_medicine_billing",
      "/medicine_payment_details",
    ],
    // ₹ symbol — India-localized + visually distinct from the
    // receipt glyph that landed too close to other Material icons.
    icon: <BiRupee size={ICON_SIZE} color={ICON_COLOR} />,
  },
  // Legacy Expense module — retired in favour of the unified module
  // below. Path renamed to `/expense_old` for reference; the route
  // entry is commented out so it's no longer mounted. Re-enable
  // (un-comment + re-add to `coreRoutes`) only if a regression makes
  // us need the old screen back.
  // {
  //   path: "/expense_old",
  //   title: "Expense (legacy)",
  //   component: ExpensePage,
  //   dashboardItem: false,
  //   icon: <FaMoneyBillTransfer size={23} color="#ffff" />,
  // },
  {
    // Unified Expense module — promoted from `/expense_v2` now that
    // the legacy route is retired. The URL drives the page header
    // text in `components/core/header/Header.js`, so the clean
    // `/expense` URL gives us a clean "Expense" header without
    // dragging the v2 suffix into the chrome.
    path: "/expense",
    title: "Expense",
    component: ExpenseV2Page,
    dashboardItem: true,
    icon: <MdRequestQuote size={ICON_SIZE} color={ICON_COLOR} />,
  },
  {
    path: "/payment_details",
    title: "PaymentDetailsPage",
    component: PaymentDetailsPage,
    dashboardItem: false,
  },
  {
    path: "/transaction_history",
    title: "TransactionHistoryPage",
    component: TransactionHistoryPage,
    dashboardItem: false,
  },
  {
    path: "/create_invoice",
    title: "CreateInvoicePage",
    component: CreateInvoicePage,
    dashboardItem: false,
  },
  {
    path: "/create_medicine_billing",
    title: "CreateMedicineBillingPage",
    component: CreateMedicineBillingPage,
    dashboardItem: false,
  },
  {
    path: "/medicine_payment_details",
    title: "MedicinePaymentDetailsPage",
    component: MedicineBillDetails,
    dashboardItem: false,
  },
  // Legacy Analytics module (old Finance dashboard) — retired in
  // favour of the unified module below. Path renamed to
  // `/analytics_old` for reference; the route entry is commented
  // out so it's no longer mounted. Re-enable only if a regression
  // forces us back to the legacy page.
  // {
  //   path: "/analytics_old",
  //   title: "Analytics (legacy)",
  //   component: FinancePage,
  //   dashboardItem: false,
  //   childrens: [],
  //   icon: <BiTrendingUp size={ICON_SIZE} color={ICON_COLOR} />,
  // },
  {
    // Unified Analytics module — promoted from `/analytics1` now
    // that the legacy route is retired. The URL drives the page
    // header text, so the clean `/analytics` URL gives us a clean
    // "Analytics" header without the version suffix bleeding in.
    path: "/analytics",
    title: "Analytics",
    component: Analytics1Page,
    dashboardItem: true,
    childrens: [],
    icon: (
      <img
        width={20}
        height={12}
        className=""
        src="/icons/Finance.svg"
        alt=""
      />
    ),
  },
  {
    path: "/settings",
    title: "Settings",
    component: Settings,
    childrens: [
      "/settings/admin_users",
      "/settings/doctors",
      "/settings/treatments",
      "/settings/medicines",
      "/settings/labs",
      "/settings/labs_works",
      "/settings/plan_and_pricing",
      "/settings/medicines_templates",
    ],
    dashboardItem: true,
    // Reverted to the original Settings.svg per request.
    icon: (
      <img
        width={18.56}
        height={18.18}
        className=""
        src="/icons/Settings.svg"
        alt=""
      />
    ),
  },
  {
    path: "/settings/plan_and_pricing",
    title: "PlanandPricingPage",
    component: PlanandPricingPage,
    dashboardItem: false,
  },
  {
    path: "/settings/admin_users",
    title: "Admin users",
    component: AdminUsers,
    dashboardItem: false,
  },
  {
    path: "/settings/doctors",
    title: "Doctors",
    component: Doctors,
    dashboardItem: false,
  },
  {
    path: "/settings/treatments",
    title: "Treatments",
    component: Treatments,
    dashboardItem: false,
  },
  {
    path: "/settings/medicines",
    title: "Medicines",
    component: Medicines,
    dashboardItem: false,
  },
  {
    path: "/settings/medicines_templates",
    title: "MedicinesTemplates",
    component: MedTemplatesPage,
    dashboardItem: false,
  },
  {
    path: "/settings/labs",
    title: "Labs",
    component: Labs,
    dashboardItem: false,
  },
  {
    // Lowercased "labs_works" so it matches the parent's
    // childrens[] entry (line ~415). Casing-mismatch was
    // breaking active-link detection AND title was also
    // duplicate of "Labs" — renamed to "Lab Works" so they
    // sort distinctly under any title-based filter.
    path: "/settings/labs_works",
    title: "Lab Works",
    component: LabsWorks,
    dashboardItem: false,
  },
];

const routes = [...coreRoutes];
export default routes;
